/**
 * @file 「AI 出力」の欄 (リーダーのページの下と、問ごとの解説の面に出す)。
 *
 * @what: いまの学習者の記録を要約の一覧で出し (資料とページ、またはメモで絞る)、選ぶと1件を取って
 *   対象 (絵つき)・やりとり (問い・答え・評価)・生成の情報を出す。評価は答えごとに1回だけ付けられ、
 *   答えが違っていたら同じ問いで作り直せる。削除は確認の上で行う。
 * @why: 答えだけ残しても後で読めないので、対象と問いと組で見せる。「誤り」の評価が付いた答えは
 *   消さずに印を付けて出す (後で読み返して誤りのほうを覚えないため)。作り直したときは、前の答えを
 *   畳んで出す (残したまま、読み返しで目に入らないようにする)。
 * @failure: 一覧の取得失敗は1行で知らせる。評価の409は「すでに評価がついています」と出して表示を更新する。
 *   作り直しの上限と削除の失敗は文言のみ (詳細は console)。
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import "katex/dist/katex.min.css";
import {
  ASK_RETRY_MAX,
  ASK_SCOPE_LABELS,
  ASK_STATUS_LABELS,
  askGuideLink,
  askRecordScope,
  closeAskRecord,
  deleteAskRecord,
  fetchAskRecord,
  fetchAskRecords,
  postAskTurnReaction,
  type StudyAskRecord,
  type StudyAskRecordSummary,
  ASK_MATERIAL_LABELS,
  askPresetLabel,
  streamAskTurn,
  type AIReactionReason,
  type AskScope,
  type StudyAskTarget,
  type StudyAskTurn,
} from "../api/askRecords";

import { ApiError } from "@/shared/api/client";
import { renderMathMarkdown } from "../aids/lib/mathMarkdown";
import { acquirePageImage, releasePageImage } from "../lib/pageImageCache";
import { StudyDialog } from "../components/StudyDialog";
import { ReactionBox } from "../components/ReactionBox";
import { AskStreamView } from "../components/AskStreamView";
import { ProblemGuideDialog } from "./ProblemGuideDialog";

type ListState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; records: StudyAskRecordSummary[] };

type DetailState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; record: StudyAskRecord };

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
}

export function AskRecordPanel({
  familyId,
  personId,
  sourceId,
  page,
  problemIds = [],
  noteIds = [],
  all = false,
  presetId,
  hideWhenEmpty = false,
  showScope = false,
  hideTitle = false,
}: {
  familyId: string;
  personId: string;
  /** 絞る資料 (all のときは省ける)。 */
  sourceId?: string;
  /** 拾うページをリーダーの連番で渡す (省略 = 資料全体)。 */
  page?: number;
  /** 拾う問の id (問題ごとの解説の面で使う)。 */
  problemIds?: readonly string[];
  /** このページのメモの id。メモを対象にした記録もここで拾う。 */
  noteIds?: readonly string[];
  /** 資料やページで絞らず、この学習者の記録を全部出す (一覧の面で使う)。 */
  all?: boolean;
  /** 操作で絞る (ai_explain / ai_grade)。省略 = 絞らない。 */
  presetId?: string;
  /** 1件も無いとき、見出しごと出さない (読み込み失敗は文言を出す)。 */
  hideWhenEmpty?: boolean;
  /** 行に「同じページ」などの範囲を出す (問と同じページの記録が混ざる面で使う)。 */
  showScope?: boolean;
  /** 自前の見出しと上の区切り線を出さない (面が見出しを持つとき)。all のときは常に出さない。 */
  hideTitle?: boolean;
}) {
  const bare = all || hideTitle;
  const [list, setList] = useState<ListState>({ status: "loading" });
  const [openId, setOpenId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setList({ status: "loading" });
    fetchAskRecords(familyId, personId, controller.signal)
      .then((records) => {
        if (!controller.signal.aborted) setList({ status: "ready", records });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load ask records", err);
        setList({ status: "error" });
      });
    return () => controller.abort();
  }, [familyId, personId, reloadKey]);

  const visible = useMemo(() => {
    if (list.status !== "ready") return [];
    const rows: { record: StudyAskRecordSummary; scope: AskScope | null }[] = [];
    for (const r of list.records) {
      if (presetId !== undefined && r.first_preset !== presetId) continue;
      if (all) {
        rows.push({ record: r, scope: null });
        continue;
      }
      // 資料が無い絞り込み (メモで絞る一覧の面) は、メモか問で当たったものだけを拾う。
      if (sourceId === undefined && noteIds.length === 0 && problemIds.length === 0) continue;
      const scope = askRecordScope(r.targets, sourceId, page, noteIds, problemIds);
      if (scope !== null) rows.push({ record: r, scope });
    }
    // 問そのものに紐づくものを先に出す (同じページというだけのものは後ろ)。
    return rows.sort((a, b) => scopeOrder(a.scope) - scopeOrder(b.scope));
  }, [list, sourceId, page, noteIds, problemIds, all, presetId]);

  const onDeleted = useCallback(() => {
    setOpenId(null);
    setReloadKey((k) => k + 1);
  }, []);

  if (hideWhenEmpty && list.status !== "error" && visible.length === 0) return null;

  return (
    <section className={bare ? "study-ask study-ask--all" : "study-ask"} aria-label="AI 出力">
      {bare ? null : <h3 className="study-ask-title">AI 出力</h3>}
      {list.status === "loading" ? (
        <p className="study-muted" role="status">
          読み込んでいます...
        </p>
      ) : list.status === "error" ? (
        <p className="study-error" role="alert">
          AI 出力を読み込めませんでした。
        </p>
      ) : visible.length === 0 ? (
        <p className="study-muted">まだありません。</p>
      ) : (
        <ul className="study-ask-list">
          {visible.map(({ record: r, scope }) => {
            const scopeLabel = showScope && scope ? ASK_SCOPE_LABELS[scope] : undefined;
            return (
              <li
                key={r.id}
                className={r.id === openId ? "study-ask-row is-open" : "study-ask-row"}
              >
                <button
                  type="button"
                  className="study-ask-row-button"
                  aria-expanded={r.id === openId}
                  onClick={() => setOpenId(r.id === openId ? null : r.id)}
                >
                  <span className="study-ask-row-date">{formatDate(r.created_at)}</span>
                  <span className="study-ask-op">{askPresetLabel(r.first_preset)}</span>
                  {scopeLabel ? (
                    <span className="study-ask-chip study-ask-chip--scope">{scopeLabel}</span>
                  ) : null}
                  <span className="study-ask-row-label">{targetsLabel(r.targets)}</span>
                  <span className={`study-ask-chip study-ask-chip--${r.status}`}>
                    {ASK_STATUS_LABELS[r.status]}
                  </span>
                  {r.has_wrong_answer ? (
                    <span className="study-ask-chip study-ask-chip--wrong">誤りの指摘あり</span>
                  ) : null}
                  {r.turn_count > 1 ? (
                    <span className="study-ask-chip">{r.turn_count}往復</span>
                  ) : null}
                  {/* 定型の問いは操作の名前と同じ文なので、行に二度出さない */}
                  {r.first_preset ? null : (
                    <span className="study-ask-row-head">{r.first_prompt}</span>
                  )}
                </button>
                {r.id === openId ? (
                  <AskRecordLoader
                    familyId={familyId}
                    personId={personId}
                    recordId={r.id}
                    openProblemIds={problemIds}
                    onDeleted={onDeleted}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** 並び順 (問そのもの → メモ → 同じページ → 資料全体)。 */
function scopeOrder(scope: AskScope | null): number {
  switch (scope) {
    case "problem":
      return 0;
    case "note":
      return 1;
    case "page":
      return 2;
    default:
      return 3;
  }
}

function targetsLabel(targets: readonly StudyAskTarget[]): string {
  const labels = targets.map((t) => t.label || t.ref);
  return labels.length <= 2 ? labels.join(" + ") : `${labels[0]} ほか${labels.length - 1}件`;
}

function AskRecordLoader({
  familyId,
  personId,
  recordId,
  openProblemIds,
  onDeleted,
}: {
  familyId: string;
  personId: string;
  recordId: string;
  /** いま開いている問 (その解説への線は出さない)。 */
  openProblemIds?: readonly string[];
  onDeleted: () => void;
}) {
  const [state, setState] = useState<DetailState>({ status: "loading" });
  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading" });
    fetchAskRecord(familyId, personId, recordId, controller.signal)
      .then((record) => {
        if (!controller.signal.aborted) setState({ status: "ready", record });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load ask record", err);
        setState({ status: "error" });
      });
    return () => controller.abort();
  }, [familyId, personId, recordId]);

  if (state.status === "loading") {
    return (
      <p className="study-muted" role="status">
        読み込んでいます...
      </p>
    );
  }
  if (state.status === "error") {
    return (
      <p className="study-error" role="alert">
        AI 出力を読み込めませんでした。
      </p>
    );
  }
  return (
    <AskRecordDetail
      record={state.record}
      familyId={familyId}
      guideLink={askGuideLink(state.record.targets, familyId, openProblemIds)}
      onReact={async (turnIndex, body) => {
        const updated = await postAskTurnReaction(familyId, personId, recordId, turnIndex, body);
        setState({ status: "ready", record: updated });
      }}
      onReload={async () => {
        const fresh = await fetchAskRecord(familyId, personId, recordId);
        setState({ status: "ready", record: fresh });
      }}
      onClose={async () => {
        const closed = await closeAskRecord(familyId, personId, recordId);
        setState({ status: "ready", record: closed });
      }}
      onDelete={async () => {
        await deleteAskRecord(familyId, personId, recordId);
        onDeleted();
      }}
    />
  );
}

/**
 * 作り直されて、後ろに新しい答えがあるやりとりの番号を返す。
 *
 * @why: 誤りだった答えを消さずに残しつつ、読み返しで目に入らないように畳むためである。
 */
function supersededTurns(turns: readonly StudyAskTurn[]): Set<number> {
  const out = new Set<number>();
  for (const t of turns) {
    if (t.retry_of !== undefined) out.add(t.retry_of);
  }
  return out;
}

/** そのやりとりが何回目の作り直しかを返す (作り直しでなければ0)。 */
function retryDepth(turns: readonly StudyAskTurn[], index: number): number {
  let depth = 0;
  let at = index;
  while (at >= 0 && at < turns.length) {
    const from = turns[at]?.retry_of;
    if (from === undefined) break;
    depth += 1;
    if (from >= at) break;
    at = from;
  }
  return depth;
}

/** 1件の本文 (描画専用である。取得と保存は親が渡す)。 */
export function AskRecordDetail({
  record,
  familyId,
  guideLink,
  onReact,
  onReload,
  onClose,
  onDelete,
}: {
  record: StudyAskRecord;
  familyId: string;
  /** 紐づく問 (無ければ解説の線を出さない)。押すと解説をこの面の上に重ねて出す。 */
  guideLink?: { href: string; problemId: string; sourceId: string } | null;
  onReact: (
    turnIndex: number,
    body: { helpful: boolean; reasons?: AIReactionReason[]; comment?: string },
  ) => Promise<void>;
  onReload: () => Promise<void>;
  onClose: () => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // 同じ対象のまま続けて聞く (対象は記録が持っているので選び直さない)。
  const [again, setAgain] = useState("");
  const [asking, setAsking] = useState(false);
  // 作っている途中の答えである (届いた分をその場に出す)。null = 出さない。
  const [partial, setPartial] = useState<string | null>(null);
  // 答えを作り直しているやりとりの番号 (null = 続けて聞いている / 何もしていない)。
  const [retrying, setRetrying] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  // 紐づく問の解説を、面を移らずに重ねて読む。
  const [guideOpen, setGuideOpen] = useState(false);

  const superseded = useMemo(() => supersededTurns(record.turns), [record.turns]);
  const closed = record.status === "closed";

  const askAgain = async () => {
    if (asking || again.trim() === "") return;
    setAsking(true);
    setNotice(null);
    setPartial("");
    try {
      await streamAskTurn(
        familyId,
        record.learner_person_id,
        record.id,
        { question: again.trim() },
        (chunk) => setPartial((got) => (got ?? "") + chunk),
      );
      setAgain("");
      setPartial(null);
      await onReload();
    } catch (err: unknown) {
      setPartial(null);
      console.error("[study] failed to ask again", err);
      setNotice(
        err instanceof ApiError && err.status === 409
          ? "この記録は終わりにしているので、続けて聞けません。新しく選んで聞いてください。"
          : "続けて聞けませんでした。時間を置いて、もう一度押してください。",
      );
    } finally {
      setAsking(false);
    }
  };

  /** 同じ問いのまま、答えをもう一度作る (前の答えは残って畳まれる)。 */
  const retry = async (turnIndex: number, note: string) => {
    if (asking) return;
    setAsking(true);
    setRetrying(turnIndex);
    setNotice(null);
    setPartial("");
    try {
      await streamAskTurn(
        familyId,
        record.learner_person_id,
        record.id,
        {
          retry_of_turn: turnIndex,
          ...(note.trim() ? { retry_note: note.trim() } : {}),
        },
        (chunk) => setPartial((got) => (got ?? "") + chunk),
      );
      setPartial(null);
      await onReload();
    } catch (err: unknown) {
      setPartial(null);
      console.error("[study] failed to redo an ask turn", err);
      setNotice(
        err instanceof ApiError && err.status === 409
          ? "この答えは、これ以上作り直せません。"
          : "答えを作り直せませんでした。時間を置いて、もう一度押してください。",
      );
    } finally {
      setAsking(false);
      setRetrying(null);
    }
  };

  const react = async (
    turnIndex: number,
    body: { helpful: boolean; reasons?: AIReactionReason[]; comment?: string },
  ) => {
    setBusy(true);
    setNotice(null);
    try {
      await onReact(turnIndex, body);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409) {
        setNotice("すでに評価がついています。");
        await onReload();
      } else {
        console.error("[study] failed to react to an ask turn", err);
        setNotice("評価を送れませんでした。");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="study-ask-detail">
      <button
        type="button"
        className="study-ask-delete"
        aria-label="この記録を消す"
        title="この記録を消す"
        onClick={() => setConfirming(true)}
      >
        <TrashIcon />
      </button>

      <section className="study-ask-section">
        <h4>質問</h4>
        <ul className="study-ask-targets">
          {record.targets.map((t, i) => (
            <li key={`${t.ref}-${i}`} className="study-ask-target">
              <span>{t.label || t.ref}</span>
              {t.image_path ? <TargetImage path={t.image_path} label={t.label || t.ref} /> : null}
            </li>
          ))}
        </ul>
        {record.self_report ? (
          <div className="study-ask-self">
            <span className="study-ask-self-label">補足したこと</span>
            <p className="study-ask-self-body">{record.self_report}</p>
          </div>
        ) : null}
        {guideLink ? (
          <p className="study-ask-guide-link">
            <button
              type="button"
              className="study-ask-guide-link-btn"
              onClick={() => setGuideOpen(true)}
            >
              この問題の解説を読む
            </button>
          </p>
        ) : null}
        {guideOpen && guideLink ? (
          <ProblemGuideDialog
            familyId={familyId}
            sourceId={guideLink.sourceId}
            problemId={guideLink.problemId}
            onClose={() => setGuideOpen(false)}
          />
        ) : null}
      </section>

      {record.turns.map((turn, i) => {
        const body = (
          <>
            <div
              className="study-guides-md study-ask-answer"
              dangerouslySetInnerHTML={{ __html: renderMathMarkdown(turn.answer) }}
            />
            {turn.materials && turn.materials.length > 0 ? (
              <details className="study-ask-materials">
                <summary>読んだ材料 ({turn.materials.length})</summary>
                <ul className="study-ask-material-list">
                  {turn.materials.map((m, i) => (
                    <li key={`${m.kind}-${i}`}>
                      <span className="study-ask-material-kind">{ASK_MATERIAL_LABELS[m.kind]}</span>
                      {m.label ?? ""}
                      {m.image ? "（画像）" : m.chars ? `（${m.chars}字）` : ""}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
            <p className="study-ask-generation">
              {turn.generation.model} / {turn.generation.prompt_version} /{" "}
              {formatDate(turn.generation.generated_at)}
            </p>
            <ReactionBox
              reaction={turn.reaction}
              subject="この答えは"
              busy={busy}
              onReact={(reaction) => react(i, reaction)}
            />
          </>
        );
        const head = (
          <>
            <span className="study-ask-op">{askPresetLabel(turn.preset_id)}</span>
            {turn.prompt_kind === "preset" ? null : turn.prompt}
            {turn.retry_of !== undefined ? (
              <span className="study-ask-chip">{retryDepth(record.turns, i)}回目の出力</span>
            ) : null}
            {turn.reaction && turn.reaction.reasons?.includes("wrong") ? (
              <span className="study-ask-chip study-ask-chip--wrong">誤りの指摘あり</span>
            ) : null}
          </>
        );
        // 作り直された答えは畳んで出す。消さずに残すが、開かないと読めない。
        if (superseded.has(i)) {
          return (
            <details key={i} className="study-ask-section study-ask-turn study-ask-turn--old">
              <summary>
                <span className="study-ask-chip study-ask-chip--old">作り直す前の答え</span>
                {head}
              </summary>
              {body}
            </details>
          );
        }
        return (
          <section key={i} className="study-ask-section study-ask-turn">
            <h4>{head}</h4>
            {/* 本人が書いた文なので、AI の答えと地の文で並べず、囲いで分ける */}
            {turn.retry_note ? (
              <div className="study-ask-self">
                <span className="study-ask-self-label">作り直しのときに書いたこと</span>
                <p className="study-ask-self-body">{turn.retry_note}</p>
              </div>
            ) : null}
            {body}
            {closed || retryDepth(record.turns, i) >= ASK_RETRY_MAX ? null : (
              <RetryForm
                id={`${record.id}-${i}`}
                busy={asking}
                onRetry={(note) => retry(i, note)}
              />
            )}
            {retrying === i && partial !== null ? <AskStreamView text={partial} /> : null}
          </section>
        );
      })}

      {closed ? (
        <p className="study-ask-hint">この記録は終わりにしています。</p>
      ) : (
        <>
          <form
            className="study-ask-again"
            onSubmit={(e) => {
              e.preventDefault();
              void askAgain();
            }}
          >
            <label className="study-ask-again-label" htmlFor={`study-ask-again-${record.id}`}>
              同じところについて、続けて聞く
            </label>
            <span className="study-ask-again-row">
              <input
                id={`study-ask-again-${record.id}`}
                type="text"
                className="study-ask-again-input"
                placeholder="聞くこと"
                maxLength={500}
                value={again}
                disabled={asking}
                onChange={(e) => setAgain(e.target.value)}
              />
              <button
                type="submit"
                className="study-ghost-btn"
                disabled={asking || again.trim() === ""}
              >
                {asking ? "聞いています..." : "聞く"}
              </button>
            </span>
            {retrying === null && partial !== null ? <AskStreamView text={partial} /> : null}
          </form>
          <p className="study-ask-close">
            <button
              type="button"
              className="study-ghost-btn"
              disabled={asking || closing}
              onClick={() => setConfirmingClose(true)}
            >
              これで終わりにする
            </button>
          </p>
        </>
      )}

      {notice ? (
        <p className="study-ask-hint" role="status">
          {notice}
        </p>
      ) : null}

      {confirmingClose ? (
        <StudyDialog
          title="この記録を終わりにしますか"
          labelledBy="study-ask-close-title"
          onClose={() => (closing ? undefined : setConfirmingClose(false))}
        >
          <p>
            終わりにすると、続けて聞くことも、答えを作り直すこともできなくなります。聞いたこと・答え・
            評価は残ります。
          </p>
          <p className="study-ask-dialog-actions">
            <button
              type="button"
              className="study-ghost-btn"
              disabled={closing}
              onClick={() => setConfirmingClose(false)}
            >
              やめる
            </button>
            <button
              type="button"
              className="study-primary-btn"
              disabled={closing}
              onClick={() => {
                setClosing(true);
                void onClose()
                  .catch((err: unknown) => {
                    console.error("[study] failed to close an ask record", err);
                    setNotice("終わりにできませんでした。");
                  })
                  .finally(() => {
                    setClosing(false);
                    setConfirmingClose(false);
                  });
              }}
            >
              終わりにする
            </button>
          </p>
        </StudyDialog>
      ) : null}

      {confirming ? (
        <StudyDialog
          title="この記録を消しますか"
          labelledBy="study-ask-delete-title"
          onClose={() => (deleting ? undefined : setConfirming(false))}
        >
          <p>
            聞いたこと・答え・評価が消えます。切り取った絵の複製も消えます。学びメモは残ります。
          </p>
          <p className="study-ask-dialog-actions">
            <button
              type="button"
              className="study-ghost-btn"
              disabled={deleting}
              onClick={() => setConfirming(false)}
            >
              やめる
            </button>
            <button
              type="button"
              className="study-danger-btn"
              disabled={deleting}
              onClick={() => {
                setDeleting(true);
                void onDelete()
                  .catch((err: unknown) => {
                    console.error("[study] failed to delete an ask record", err);
                    setNotice("消せませんでした。");
                  })
                  .finally(() => {
                    setDeleting(false);
                    setConfirming(false);
                  });
              }}
            >
              消す
            </button>
          </p>
        </StudyDialog>
      ) : null}
    </div>
  );
}

/**
 * 答えを作り直す欄である。
 *
 * @what: ボタンを押すと、どこが違うかを書く欄が出る。書かずに出し直すこともできる。
 * @why: 答えが違っていたとき、聞き直しの文を考えなくても同じ問いで出し直せるようにする。
 *   一言を書けば、そこを重点的に確かめて作り直す。
 */
function RetryForm({
  id,
  busy,
  onRetry,
}: {
  id: string;
  busy: boolean;
  onRetry: (note: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");

  if (!open) {
    return (
      <p className="study-ask-retry">
        <button
          type="button"
          className="study-ghost-btn"
          disabled={busy}
          onClick={() => setOpen(true)}
        >
          再度回答を出力する
        </button>
      </p>
    );
  }
  return (
    <form
      className="study-ask-retry"
      onSubmit={(e) => {
        e.preventDefault();
        void onRetry(note).then(() => {
          setNote("");
          setOpen(false);
        });
      }}
    >
      <label className="study-ask-again-label" htmlFor={`study-ask-retry-${id}`}>
        どこが違うかを書くと、そこを確かめて作り直します
      </label>
      <span className="study-ask-again-row">
        <input
          id={`study-ask-retry-${id}`}
          type="text"
          className="study-ask-again-input"
          placeholder="どこが違うか (書かなくてもよい)"
          maxLength={500}
          value={note}
          disabled={busy}
          onChange={(e) => setNote(e.target.value)}
        />
        <button type="submit" className="study-primary-btn" disabled={busy}>
          {busy ? "作っています..." : "再度回答を出力する"}
        </button>
        <button
          type="button"
          className="study-ghost-btn"
          disabled={busy}
          onClick={() => setOpen(false)}
        >
          やめる
        </button>
      </span>
    </form>
  );
}

/** ごみ箱の絵 (絵文字だと字の色が乗らないので、線の絵で描く)。 */
function TrashIcon() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 11v5M14 11v5" />
    </svg>
  );
}

/** 対象の絵 (blob URL に解決して出す)。 */
function TargetImage({ path, label }: { path: string; label: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let acquired = false;
    acquirePageImage(path, "image/png")
      .then((u) => {
        acquired = true;
        setUrl(u);
      })
      .catch((err: unknown) => console.error("[study] failed to load a target image", err));
    return () => {
      if (acquired) releasePageImage(path);
    };
  }, [path]);
  if (!url) return null;
  return <img className="study-ask-target-image" src={url} alt={label} />;
}
